import Spinner from '@components/icons/Spinner/Spinner'
import cn from 'clsx'
import React, {
  ComponentPropsWithRef,
  ElementType,
  forwardRef,
  ReactElement,
  useRef,
} from 'react'
import mergeRefs from 'react-merge-refs'
import s from './Button.module.scss'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'link'
  | 'clean'
  | 'primary-white'
  | 'secondary-white'
  | 'link-white'
  | 'clean-white'

type ButtonProps<T extends ElementType = 'button'> =
  ComponentPropsWithRef<T> & {
    href?: string
    className?: string
    children?: React.ReactNode
    variant?: ButtonVariant
    active?: boolean
    type?: 'submit' | 'reset' | 'button'
    as?: T
    width?: string | number
    loading?: boolean
    disabled?: boolean
    icon?: ReactElement<any>
    style?: React.CSSProperties
  }

// eslint-disable-next-line react/display-name
const Button = forwardRef(
  <T extends ElementType = 'button'>(props: ButtonProps<T>, buttonRef) => {
    const {
      className,
      variant,
      children,
      active,
      width,
      icon,
      loading = false,
      disabled = false,
      style = {},
      as: Component = 'button',
      animating,
      ...rest
    } = props
    const ref = useRef<typeof Component>(null)

    const rootClassName = cn(
      {
        [s.root]: !variant.includes('clean') && !variant.includes('link'),
        [s.primary]: variant === 'primary' || variant === 'primary-white',
        [s.secondary]: variant === 'secondary' || variant === 'secondary-white',
        linkHover: variant === 'link' || variant === 'link-white',
        [s.link]: variant === 'link' || variant === 'link-white',
        [s.white]: variant.includes('white'),
        [s.animating]: animating,
        [s.loading]: loading,
        [s.disabled]: disabled,
      },
      className
    )

    return (
      <Component
        aria-pressed={active}
        data-variant={variant}
        ref={mergeRefs([ref, buttonRef])}
        className={rootClassName}
        disabled={disabled}
        style={{
          width,
          ...style,
        }}
        {...rest}
      >
        {variant === 'link' ? (
          <span className="">{children}</span>
        ) : (
          <>{children}</>
        )}
        {icon}
        {loading && (
          <i className="pl-2 m-0 flex">
            <Spinner />
          </i>
        )}
      </Component>
    )
  }
)

export default Button
